/**
 * Maritime Focus — "ships only" without leaving Cesium.
 *
 * WHY (Brockmann 2026-08-31): the vessel panel's only escape was
 * "View on MarineTraffic ↗", which on iPhone punts the operator OUT of the app
 * into Safari — losing the overlays, the tactical context and the camera, and
 * pulling a heavy third-party site over cellular. A second in-app page was the
 * other option and is worse: a second Cesium instance means a second full tile
 * download, a second AIS subscription and doubled memory on a phone.
 *
 * This is the third way: one toggle, same viewer, same camera, same AIS socket.
 * Drops every non-maritime layer, keeps ships + the satellite basemap, and
 * RESTORES exactly what was on when the operator exits. Nothing reloads.
 */

import { layers, setLayerEnabled, type LayerId } from "./layers";
import { createSignal } from "solid-js";

const [active, setActive] = createSignal(false);
/** Layer state captured on entry so exit restores precisely, not by guesswork. */
let saved: Partial<Record<LayerId, boolean>> | null = null;

export const maritimeFocusActive = active;

export function toggleMaritimeFocus(): void {
  if (active()) {
    // Exit — put back exactly what was on before, including anything the
    // operator toggled while focused.
    if (saved) {
      (Object.keys(saved) as LayerId[]).forEach((k) => setLayerEnabled(k, saved![k]!));
    }
    saved = null;
    setActive(false);
    console.log("[MaritimeFocus] OFF — previous layer state restored");
    return;
  }

  // Enter — snapshot first, then strip to ships.
  saved = {
    satellites: layers.satellites,
    ships: layers.ships,
    ground: layers.ground,
    planes: layers.planes,
  };
  setLayerEnabled("satellites", false);
  setLayerEnabled("planes", false);
  setLayerEnabled("ground", false);   // traffic / CCTV / seismic / threat / sun
  setLayerEnabled("ships", true);     // force ON — the whole point
  setActive(true);
  console.log("[MaritimeFocus] ON — ships + basemap only");
}
